import { useState } from 'react';
import Youtube, { YouTubeProps } from 'react-youtube';

import { cn } from '@virginexperiencedays/tailwind';

export type YoutubeEmbedProps = {
  /**
   * @param url - Youtube Video Url
   */
  url: string;
  className?: string;
  withSwipeFunctionality?: boolean;
  roundedBorders?: boolean;
  // prop to keep backward-compatible 100% height/width styling
  fullSize?: boolean;
};

export const YoutubeEmbed = ({
  url,
  withSwipeFunctionality,
  className,
  roundedBorders,
  fullSize = true,
}: YoutubeEmbedProps) => {
  const videoId = getVideoIdFromUrl(url);
  const [isPlaying, setIsPlaying] = useState(false);

  const opts = {
    width: '100%',
    height: '100%',
    playerVars: {
      modestbranding: 1,
      autoplay: 0,
      rel: 0,
    } as YouTubeProps['opts']['playerVars'], //overwrite cause it cries over autoplay being number not 0 or 1
  };

  function onClick() {
    setIsPlaying(true);
  }

  function onReady(event: any) {
    event.target.playVideo();
  }

  // @TODO/OPTIMISE: Refactor this in the future to be optimised, this solution brings swipe functionality by adding pseudoelement that has an onClick and updates the state to rerender embed video without it.
  // Current solution causes video to be downloaded twice, once with the pseudoelement and once when rerendered - It should be using/reffering to the same video after rerendering.
  if (withSwipeFunctionality) {
    return !isPlaying ? (
      <div
        data-testid="youtube-embed-placeholder"
        onClick={onClick}
        className={cn(
          'relative aspect-video h-full w-full after:absolute after:top-0 after:bg-transparent',
          fullSize && 'after:h-full after:min-h-[1px] after:w-full',
          className
        )}
      >
        <Youtube
          className={cn('h-full w-full', roundedBorders && 'overflow-hidden rounded')}
          videoId={videoId}
          opts={opts}
        />
      </div>
    ) : (
      <div
        className={cn('aspect-video', fullSize && 'relative h-full w-full', className)}
        data-testid="youtube-embed"
      >
        <Youtube
          className={cn(
            fullSize ? 'absolute inset-0' : 'h-full w-full',
            roundedBorders && 'overflow-hidden rounded'
          )}
          videoId={videoId}
          opts={opts}
          onReady={onReady}
          data-testid="youtube-embed-video"
        />
      </div>
    );
  }

  return (
    <div
      className={cn('aspect-video ', fullSize && 'relative h-full w-full', className)}
      data-testid={'youtube-embed'}
    >
      <Youtube className="h-full w-full" videoId={videoId} opts={opts} />
    </div>
  );
};

export function getVideoIdFromUrl(url: string): string {
  const regExpPatterns = [
    /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
    /\?v=([^#&?]{11})/, // ?v=<id>
    /&v=([^#&?]{11})/, // &v=<id>
    /embed\/([^#&?]{11})/, // embed/<id>
    /\/v\/([^#&?]{11})/, // /v/<id>
  ];

  // If any pattern matches, return the ID
  for (const pattern of regExpPatterns) {
    if (pattern.test(url)) {
      return pattern.exec(url)[1];
    }
  }

  return null;
}
